// ============================================================
// MatrizAccesoTipos.jsx — Capa 2 (acceso por tipo)
// Se renderiza DENTRO de GestorPermisos cuando se elige la entrada
// "Inventario · Acceso por tipo (Ver/Editar)" del desplegable de sección.
//
// Izquierda: destinatario (rol o usuario, selección única).
// Derecha: tabla de tipos con toggles Ver / Editar + guardar.
// Consume PeticionesTypeAccess. DS v2.
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchTiposInventario,
  fetchRolesTipoAcceso,
  fetchMatrizTipoAcceso,
  guardarMatrizTipoAcceso,
  fetchResumenTipoAcceso,
} from '../../peticiones_API/PeticionesTypeAccess';
import {
  Shield, Users, Search, X, Save, Loader2, Building, Network,
  Eye, Pencil, CheckCircle2, AlertTriangle, Box, ChevronRight,
} from 'lucide-react';
import {
  Button, Input, Select, Card, CardContent, Badge, Avatar, Checkbox, cn,
} from './ui';

const VALOR_TODOS = '__ALL__';
const SUPER_ADMIN = 'Super Administrador';

export default function MatrizAccesoTipos({ usuarios = [], unidadesNegocio = [], departamentos = [] }) {
  const [roles, setRoles] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [resumen, setResumen] = useState({ roles: {}, personas: {} }); // nº de tipos por destinatario
  const [soloConAcceso, setSoloConAcceso] = useState(false);
  const [cargandoInicial, setCargandoInicial] = useState(true);

  const [destinatario, setDestinatario] = useState(null); // { tipo:'rol'|'usuario', valor, label, sublabel }
  const [accesos, setAccesos] = useState({});              // { [asset_type_id]: { can_view, can_edit } }
  const [cargandoMatriz, setCargandoMatriz] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [hayCambios, setHayCambios] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const [busqueda, setBusqueda] = useState('');
  const [filtroNegocio, setFiltroNegocio] = useState(VALOR_TODOS);
  const [filtroDepartamento, setFiltroDepartamento] = useState(VALOR_TODOS);

  // Carga inicial: roles + tipos + resumen
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [r, t, res] = await Promise.all([
          fetchRolesTipoAcceso(),
          fetchTiposInventario(),
          fetchResumenTipoAcceso(),
        ]);
        if (!vivo) return;
        setRoles(r);
        setTipos(t);
        setResumen(res);
      } catch (e) {
        if (vivo) setMensaje({ tipo: 'error', texto: 'Error al cargar roles o tipos del inventario' });
      } finally {
        if (vivo) setCargandoInicial(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  const refrescarResumen = async () => {
    try { setResumen(await fetchResumenTipoAcceso()); } catch (e) { /* no crítico */ }
  };

  // nº de tipos asignados a un destinatario (para badge/filtro)
  const countRol = (rol) => resumen.roles[rol] || 0;
  const countUsuario = (id) => resumen.personas[String(id)] || 0;

  // Al cambiar de destinatario → cargar su matriz
  useEffect(() => {
    if (!destinatario) { setAccesos({}); setHayCambios(false); return; }
    if (destinatario.tipo === 'rol' && destinatario.valor === SUPER_ADMIN) {
      setAccesos({}); setHayCambios(false); return; // acceso total, no editable
    }
    let vivo = true;
    (async () => {
      setCargandoMatriz(true);
      setMensaje(null);
      try {
        const target = destinatario.tipo === 'rol'
          ? { role_name: destinatario.valor }
          : { person_id: destinatario.valor };
        const data = await fetchMatrizTipoAcceso(target);
        if (!vivo) return;
        const map = {};
        (data.items || []).forEach((it) => {
          map[it.asset_type_id] = { can_view: !!it.can_view, can_edit: !!it.can_edit };
        });
        setAccesos(map);
        setHayCambios(false);
      } catch (e) {
        if (vivo) setMensaje({ tipo: 'error', texto: 'Error al cargar los accesos del destinatario' });
      } finally {
        if (vivo) setCargandoMatriz(false);
      }
    })();
    return () => { vivo = false; };
  }, [destinatario]);

  const get = (tipoId) => accesos[tipoId] || { can_view: false, can_edit: false };

  const setAcceso = (tipoId, next) => {
    setAccesos((prev) => ({ ...prev, [tipoId]: next }));
    setHayCambios(true);
    setMensaje(null);
  };
  const toggleVer = (tipoId) => {
    const cur = get(tipoId);
    const can_view = !cur.can_view;
    // quitar Ver quita Editar (no se puede editar sin ver)
    setAcceso(tipoId, { can_view, can_edit: can_view ? cur.can_edit : false });
  };
  const toggleEditar = (tipoId) => {
    const cur = get(tipoId);
    const can_edit = !cur.can_edit;
    // Editar implica Ver
    setAcceso(tipoId, { can_edit, can_view: can_edit ? true : cur.can_view });
  };
  const marcarTodoVer = () => {
    const next = {};
    tipos.forEach((t) => { const c = get(t.id); next[t.id] = { can_view: true, can_edit: c.can_edit }; });
    setAccesos(next); setHayCambios(true); setMensaje(null);
  };
  const quitarTodos = () => { setAccesos({}); setHayCambios(true); setMensaje(null); };

  const guardar = async () => {
    if (!destinatario) return;
    setGuardando(true); setMensaje(null);
    try {
      const items = tipos.map((t) => ({ asset_type_id: t.id, ...get(t.id) }));
      const payload = destinatario.tipo === 'rol'
        ? { role_name: destinatario.valor, items }
        : { person_id: destinatario.valor, items };
      const data = await guardarMatrizTipoAcceso(payload);
      const map = {};
      (data.items || []).forEach((it) => {
        map[it.asset_type_id] = { can_view: !!it.can_view, can_edit: !!it.can_edit };
      });
      setAccesos(map);
      setHayCambios(false);
      setMensaje({ tipo: 'success', texto: 'Accesos guardados correctamente' });
      refrescarResumen(); // actualiza el badge del destinatario en la lista
    } catch (e) {
      const apiMsg = e.response?.data?.error || 'Error al guardar los accesos';
      setMensaje({ tipo: 'error', texto: apiMsg });
    } finally {
      setGuardando(false);
    }
  };

  const usuariosFiltrados = useMemo(() => {
    const q = busqueda.toLowerCase().trim();
    const negocioId = filtroNegocio !== VALOR_TODOS ? parseInt(filtroNegocio, 10) : null;
    const depId = filtroDepartamento !== VALOR_TODOS ? parseInt(filtroDepartamento, 10) : null;
    return usuarios.filter((u) => {
      if (soloConAcceso && countUsuario(u.Id) === 0) return false;
      if (q) {
        const m = u.nombre?.toLowerCase().includes(q) || u.apellidos?.toLowerCase().includes(q) ||
          u.mail_empresa?.toLowerCase().includes(q) || u.rol?.toLowerCase().includes(q);
        if (!m) return false;
      }
      if (negocioId !== null && !u.unidadesNegocio?.some((n) => n.Id === negocioId)) return false;
      if (depId !== null && !u.departamentos?.some((d) => d.Id === depId)) return false;
      return true;
    });
  }, [usuarios, busqueda, filtroNegocio, filtroDepartamento, soloConAcceso, resumen]);

  const rolesFiltrados = useMemo(
    () => (soloConAcceso ? roles.filter((r) => countRol(r) > 0) : roles),
    [roles, soloConAcceso, resumen]
  );

  const seleccionarRol = (rol) => setDestinatario({ tipo: 'rol', valor: rol, label: rol });
  const seleccionarUsuario = (u) => setDestinatario({
    tipo: 'usuario', valor: u.Id,
    label: `${u.nombre || ''} ${u.apellidos || ''}`.trim(),
    sublabel: u.rol || 'Sin rol',
  });

  const esSel = (tipo, valor) => destinatario?.tipo === tipo && destinatario.valor === valor;
  const esSuperAdminSel = destinatario?.tipo === 'rol' && destinatario.valor === SUPER_ADMIN;

  const totalVer = tipos.filter((t) => get(t.id).can_view).length;
  const totalEditar = tipos.filter((t) => get(t.id).can_edit).length;

  if (cargandoInicial) {
    return (
      <div className="py-10 text-center text-slate-500 text-sm">
        <Loader2 className="size-5 text-[hsl(var(--brand-light))] mx-auto animate-spin mb-2" />
        Cargando roles y tipos…
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

      {/* DESTINATARIO (col-span-5) */}
      <Card className="lg:col-span-5 h-fit">
        <CardContent className="p-4">
          {/* Filtro: solo destinatarios con acceso asignado */}
          <label className="flex items-center gap-2 mb-3 text-xs text-slate-600 cursor-pointer select-none">
            <Checkbox checked={soloConAcceso} onChange={() => setSoloConAcceso((v) => !v)} />
            <span>Mostrar solo roles/usuarios <span className="font-medium text-slate-700">con acceso</span></span>
          </label>

          {/* Roles */}
          <div className="flex items-center gap-2 mb-1">
            <Shield className="size-4 text-[hsl(var(--brand-light))]" strokeWidth={1.75} />
            <div className="text-sm font-semibold text-slate-900">Roles</div>
          </div>
          <div className="text-[11px] text-slate-500 mb-3">Acceso por tipo para todo un rol</div>
          <div className="space-y-0.5 mb-4">
            {rolesFiltrados.length === 0 ? (
              <div className="px-2.5 py-2 text-xs text-slate-400 italic">Ningún rol con acceso asignado</div>
            ) : rolesFiltrados.map((rol) => {
              const sa = rol === SUPER_ADMIN;
              const sel = esSel('rol', rol);
              const n = countRol(rol);
              return (
                <button
                  key={rol}
                  type="button"
                  onClick={() => seleccionarRol(rol)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm text-left transition-colors',
                    sel ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'
                  )}
                >
                  <Shield size={14} className={sel ? 'text-white' : 'text-slate-400'} strokeWidth={1.75} />
                  <span className="flex-1">{rol}</span>
                  {sa ? (
                    <span className={cn('text-[9px] uppercase tracking-wider font-medium', sel ? 'text-white/70' : 'text-slate-400')}>
                      Siempre
                    </span>
                  ) : n > 0 && (
                    <span className={cn(
                      'text-[10px] font-semibold rounded-full px-1.5 py-0.5 tabular-nums',
                      sel ? 'bg-white/20 text-white' : 'bg-[hsl(var(--brand-light))]/10 text-[hsl(var(--brand-light))]'
                    )}>
                      {n}
                    </span>
                  )}
                  {sel && <ChevronRight size={14} className="text-white/70" />}
                </button>
              );
            })}
          </div>

          {/* Usuarios */}
          <div className="flex items-center gap-2 mb-1">
            <Users className="size-4 text-[hsl(var(--brand-light))]" strokeWidth={1.75} />
            <div className="text-sm font-semibold text-slate-900">Usuarios</div>
          </div>
          <div className="text-[11px] text-slate-500 mb-3">Acceso por tipo para un usuario concreto</div>

          <div className="mb-2.5">
            <Input
              placeholder="Buscar usuario..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              prefix={<Search size={14} />}
              suffix={busqueda && (
                <button onClick={() => setBusqueda('')} type="button"
                  className="p-0.5 text-slate-400 hover:text-slate-700 transition-colors" aria-label="Limpiar búsqueda">
                  <X size={14} />
                </button>
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Select value={filtroNegocio} onChange={(e) => setFiltroNegocio(e.target.value)}
              prefix={<Building size={13} />} className="text-xs">
              <option value={VALOR_TODOS}>Todas las unidades</option>
              {unidadesNegocio.map((e) => <option key={e.Id} value={e.Id}>{e.nombre}</option>)}
            </Select>
            <Select value={filtroDepartamento} onChange={(e) => setFiltroDepartamento(e.target.value)}
              prefix={<Network size={13} />} className="text-xs">
              <option value={VALOR_TODOS}>Todos los departamentos</option>
              {departamentos.map((d) => <option key={d.Id} value={d.Id}>{d.nombre}</option>)}
            </Select>
          </div>

          <div className="max-h-[320px] overflow-y-auto border border-slate-200 rounded-lg">
            {usuariosFiltrados.length === 0 ? (
              <div className="p-5 text-center text-slate-500 text-sm italic">No se han encontrado usuarios</div>
            ) : (
              usuariosFiltrados.map((u, idx) => {
                const nombre = `${u.nombre || ''} ${u.apellidos || ''}`.trim();
                const sel = esSel('usuario', u.Id);
                const n = countUsuario(u.Id);
                return (
                  <button
                    key={u.Id}
                    type="button"
                    onClick={() => seleccionarUsuario(u)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors',
                      sel ? 'bg-slate-900 text-white' : 'hover:bg-slate-50',
                      idx < usuariosFiltrados.length - 1 && !sel && 'border-b border-slate-100'
                    )}
                  >
                    <Avatar name={nombre} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className={cn('font-medium truncate', sel ? 'text-white' : 'text-slate-900')}>{nombre}</div>
                      <div className={cn('text-[11px] mt-0.5 truncate', sel ? 'text-white/70' : 'text-slate-500')}>
                        {u.rol || 'Sin rol'}{u.mail_empresa && ` · ${u.mail_empresa}`}
                      </div>
                    </div>
                    {n > 0 && (
                      <span className={cn(
                        'text-[10px] font-semibold rounded-full px-1.5 py-0.5 tabular-nums shrink-0',
                        sel ? 'bg-white/20 text-white' : 'bg-[hsl(var(--brand-light))]/10 text-[hsl(var(--brand-light))]'
                      )}>
                        {n}
                      </span>
                    )}
                    {sel && <ChevronRight size={14} className="text-white/70" />}
                  </button>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* MATRIZ DE TIPOS (col-span-7) */}
      <Card className="lg:col-span-7">
        <CardContent className="p-4">
          {!destinatario ? (
            <div className="py-16 text-center text-slate-400">
              <Box className="size-7 mx-auto mb-3" strokeWidth={1.5} />
              <div className="text-sm">Selecciona un rol o un usuario para definir a qué tipos accede.</div>
            </div>
          ) : esSuperAdminSel ? (
            <div className="py-16 text-center text-slate-500">
              <Shield className="size-7 mx-auto mb-3 text-[hsl(var(--brand-light))]" strokeWidth={1.5} />
              <div className="text-sm font-medium text-slate-700">Super Administrador</div>
              <div className="text-sm mt-1">Tiene acceso total a todos los tipos, siempre. No requiere configuración.</div>
            </div>
          ) : (
            <>
              {/* Cabecera destinatario + acciones */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{destinatario.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {destinatario.tipo === 'rol' ? 'Rol' : destinatario.sublabel}
                    {' · '}{totalVer} con vista · {totalEditar} con edición
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-xs">
                  <button type="button" onClick={marcarTodoVer}
                    className="text-[hsl(var(--brand-light))] hover:underline font-medium">Marcar todo Ver</button>
                  <button type="button" onClick={quitarTodos}
                    className="text-slate-500 hover:underline font-medium">Quitar todos</button>
                </div>
              </div>

              {cargandoMatriz ? (
                <div className="py-10 text-center text-slate-500 text-sm">
                  <Loader2 className="size-5 text-[hsl(var(--brand-light))] mx-auto animate-spin mb-2" />
                  Cargando accesos…
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  {/* Cabecera tabla */}
                  <div className="flex items-center px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    <div className="flex-1">Tipo de artículo</div>
                    <div className="w-20 text-center flex items-center justify-center gap-1"><Eye size={12} /> Ver</div>
                    <div className="w-20 text-center flex items-center justify-center gap-1"><Pencil size={12} /> Editar</div>
                  </div>
                  {tipos.length === 0 ? (
                    <div className="p-5 text-center text-slate-500 text-sm italic">No hay tipos de inventario.</div>
                  ) : (
                    tipos.map((t, idx) => {
                      const a = get(t.id);
                      return (
                        <div key={t.id} className={cn(
                          'flex items-center px-3 py-2.5 text-sm hover:bg-slate-50 transition-colors',
                          idx < tipos.length - 1 && 'border-b border-slate-100'
                        )}>
                          <div className="flex-1 flex items-center gap-2 min-w-0">
                            <span className="text-slate-900 font-medium truncate">{t.nombre}</span>
                            {t.scope === 'colab' && <Badge variant="secondary">Colab</Badge>}
                          </div>
                          <div className="w-20 flex justify-center">
                            <Checkbox checked={a.can_view} onChange={() => toggleVer(t.id)} />
                          </div>
                          <div className="w-20 flex justify-center">
                            <Checkbox checked={a.can_edit} onChange={() => toggleEditar(t.id)} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Mensaje */}
              {mensaje && (
                <div className={cn(
                  'mt-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2',
                  mensaje.tipo === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                )}>
                  {mensaje.tipo === 'success' ? <CheckCircle2 className="size-4 shrink-0" /> : <AlertTriangle className="size-4 shrink-0" />}
                  {mensaje.texto}
                </div>
              )}

              {/* Guardar */}
              <div className="mt-4 flex justify-end items-center gap-3">
                {hayCambios && <span className="text-xs text-amber-600 italic">Hay cambios sin guardar</span>}
                <Button onClick={guardar} disabled={!hayCambios || guardando}>
                  {guardando ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
                  {guardando ? 'Guardando…' : 'Guardar accesos'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
